/**
 * 상품 관련 API 서비스
 *
 * API 클라이언트 인스턴스를 인자로 받아 호출 (DI 패턴)
 * shared 레이어: config, workspace-pkg만 import 가능
 *
 * Wave 1: curl 검증 결과 기반 타입 수정 + standalone 함수 제거 (이중 API surface 통합)
 */

import type { ApiClient } from "@/shared/api/client";

// ── 상품 목록 아이템 (GET /products 응답의 list[]) ──

export interface ProductListItem {
  id: number;
  name: string;
  idBrand: number;
  imagePath: string;
  goodsPrice: number;
  fixedPrice: number;
  "Brand.name": string;
}

// ── 찜 목록 아이템 (GET /products/favorite/list 응답) ──

export interface FavoriteListItem {
  id: number;
  idBrand: number;
  title: string;
  price: number;
  msrp: number;
  img: string;
  createdAt: string;
  idFavorite: number;
}

// ── 페이지네이션 (GET /products 응답의 pagination) ──

export interface ProductPagination {
  data_count: number;
  current_page: string;
  total_page: number;
  pagesize: number;
}

// ── 상품 목록 응답 래퍼 ──

/**
 * ✅ curl verified
 * - /products returns envelope { code, data: { products: {...} } } → unwrap → { products }
 * - /search/products returns { product: {...} } (singular, no envelope) → normalized in searchProducts()
 */
export interface ProductListResponse {
  products: {
    list: ProductListItem[];
    pagination: ProductPagination;
    season?: {
      key: string;
      categoryId: number;
      label: string;
      startDate: string;
      endDate: string;
      calendarStatus: "ok" | "unsupported-year";
      nextTransitionAt: string;
    };
  };
}

// ── 상품 상세 (GET /products/{id}) ──

export interface ProductDetailData {
  id: number;
  name: string;
  idBrand: number;
  idCategory: number;
  prodStatus: string;
  imagePath: string;
  goodsPrice: number;
  fixedPrice: number;
  is_exist_options: "Y" | "N";
  minOrderCnt: number;
  content: string;
  shortDescription: string;
  "Brand.name": string;
  totalStock: number;
  salesUnit: number;
  maxOrderCnt: number;
  wishCnt: number;
  isOnlineSale: number;
  isImageUsage: number;
  regDt: string;
  modDt: string;
}

export interface ProductOptionVariant {
  id: number;
  idProductOption: number;
  code: string;
  name: string;
  addedPrice: number;
  isOnSale: number;
}

export interface ProductOption {
  id: number;
  idProduct: number;
  code: string;
  name: string;
  isRequire: number;
  variant: ProductOptionVariant[];
}

export interface OptionDetail {
  id: number;
  idProduct: number;
  price: number;
  VariantList: string;
  is_require: number;
  stock: number;
  isOnSale: number;
  deletedAt: string | null;
}

export interface ProductDetailResponse {
  product: ProductDetailData;
  options: ProductOption[];
  options_details: OptionDetail[];
}

// ── 인기상품 (필드명이 다른 API 응답) ──

export interface PopularProductRaw {
  id: number;
  title: string;
  idBrand: number;
  brandName?: string;
  img: string;
  price: number;
  msrp: number;
  score: number;
  total_purchases: number;
  unique_users: number;
  repeat_purchase_rate: number;
}

/** 홈 리스팅 상품 응답 (GET /home/popular-products, /home/new-products) */
export type HomeProductRaw = Pick<
  PopularProductRaw,
  "id" | "title" | "idBrand" | "brandName" | "img" | "price" | "msrp"
>;

/** 인기상품 API 응답 → ProductListItem 변환 */
export function toProductListItem(raw: HomeProductRaw): ProductListItem {
  return {
    id: raw.id,
    name: raw.title,
    idBrand: raw.idBrand,
    imagePath: raw.img,
    goodsPrice: raw.price,
    fixedPrice: raw.msrp,
    "Brand.name": raw.brandName ?? "",
  };
}

// ── 브랜드 ──

export interface Brand {
  id: number;
  name: string;
  thumbnail: string;
}

// ── 브랜드 정보 ──

/** ✅ curl verified 2026-04-12 — GET /brands/information/{id} */
export interface BrandInfo {
  id: number;
  name: string;
  logoImageUrl: string;
  backgroundImageUrl: string | null;
  description: string;
  minimumOrderValue: number;
  discountRate: string;
  basicShippingFee: number;
  remoteAreaShippingFee: number;
  jejuShippingFee: number;
  homepagUrl: string;
  isImageAllowed: number;
  isOnlineSalesAllowed: number;
}

// ── 브랜드 공지 ──

/** ✅ verified against legacy backend 2026-04-19 — GET /brandNotice/{idBrand}/latest/exposed (envelope unwrapped: response.data = notice) */
export interface BrandNotice {
  id: number;
  brandId: number;
  title: string;
  content: string;
  isHtmlContent: boolean;
  isExposed: boolean;
  isAlwaysExposed: boolean;
  exposureStartDate: string | null;
  exposureEndDate: string | null;
  createdBy: string | null;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

// ── 브랜드별 상품 응답 (envelope A 내부) ──

/** ✅ curl verified 2026-04-12 — GET /products/brand */
export interface BrandProductsResponse {
  brand: { name: string };
  list: ProductListItem[];
  pagination: ProductPagination;
}

// ── 상품 목록 조회 파라미터 ──

export interface GetProductsParams {
  page?: number;
  /** 백엔드 숫자 카테고리 ID (0=전체) */
  idCategory?: string;
  /** 정렬: best, latest, recommend */
  order?: string;
  /** 레거시 필터: price:min-max 형식 */
  filters?: string;
  brand?: string;
  sort?: string;
  segment?: string;
}

// ── 브랜드명 보강 헬퍼 ──

/** idBrand → name 맵을 병렬 fetch. 실패한 브랜드는 무시. */
async function fetchBrandMap(client: ApiClient, brandIds: number[]): Promise<Map<number, string>> {
  const unique = [...new Set(brandIds)];
  const map = new Map<number, string>();
  await Promise.allSettled(
    unique.map(async (id) => {
      try {
        const res = await client.get<BrandInfo>(`/brands/information/${id}`);
        map.set(id, res.data.name);
      } catch {
        /* skip */
      }
    }),
  );
  return map;
}

// ── 서비스 팩토리 (단일 API surface) ──

export function createProductService(client: ApiClient) {
  return {
    /** 상품 목록 조회 GET /products */
    getProducts: (params?: Record<string, string | number | boolean | undefined>) =>
      client.get<ProductListResponse>("/products", { params }),

    /** 상품 상세 조회 GET /products/{id} */
    getProductDetail: (id: string) => client.get<ProductDetailResponse>(`/products/${id}`),

    /** 상품 검색 GET /search/products?q= — response normalized + brand names enriched */
    searchProducts: async (params: { keyword: string; page?: number }) => {
      const res = await client.get<{
        product: {
          list: Array<Omit<ProductListItem, "Brand.name"> & { idBrand: number }>;
          pagination: ProductPagination;
        };
      }>("/search/products", {
        params: { q: params.keyword, page: params.page } as Record<
          string,
          string | number | boolean | undefined
        >,
      });
      const items = res.data.product.list;
      const brandMap = await fetchBrandMap(
        client,
        items.map((i) => i.idBrand),
      );
      const enriched = items.map((item) => ({
        ...item,
        "Brand.name": brandMap.get(item.idBrand) ?? "",
      }));
      return {
        ...res,
        data: {
          products: { list: enriched, pagination: res.data.product.pagination },
        } as ProductListResponse,
      };
    },

    /** 인기 상품 GET /products/popular */
    getPopularProducts: () =>
      client.get<{ products: PopularProductRaw[] }>("/products/category/popular/all"),

    /** 브랜드별 상품 GET /products/brand — Brand.name normalized from nested Brand */
    getBrandProducts: async (params: {
      idBrand: string;
      page?: number;
      order?: string;
      search?: string;
    }) => {
      const res = await client.get<BrandProductsResponse>("/products/brand", {
        params: params as Record<string, string | number | boolean | undefined>,
      });
      const brandName = res.data.brand.name;
      const list = res.data.list.map((item) => ({ ...item, "Brand.name": brandName }));
      return { ...res, data: { ...res.data, list } };
    },

    /** 인기 브랜드 GET /brands/popular-list */
    getPopularBrands: () => client.get<{ brands: Brand[] }>("/brands/popular-list"),

    /** 신규 브랜드 GET /brands/new-brands */
    getNewBrands: () => client.get<{ brands: Brand[] }>("/brands/new-brands"),

    /** 홈 인기 상품 25개 GET /home/popular-products */
    getHomePopularProducts: () =>
      client.get<{ products: HomeProductRaw[] }>("/home/popular-products"),

    /** 홈 신제품 12개 (브랜드당 최대 2개) GET /home/new-products */
    getHomeNewProducts: () => client.get<{ products: HomeProductRaw[] }>("/home/new-products"),

    /** 홈 인기 브랜드 30개 GET /home/popular-brands */
    getHomePopularBrands: () => client.get<{ brands: Brand[] }>("/home/popular-brands"),

    /** 홈 주목할 브랜드 10개 (매일 순환) GET /home/featured-brands */
    getHomeFeaturedBrands: () => client.get<{ brands: Brand[] }>("/home/featured-brands"),

    /** 상품 카드 배지 ("인기 브랜드" 상위 30개, "인기" 재구매 상품) GET /home/badges */
    getHomeBadges: () =>
      client.get<{ popularBrandIds: number[]; popularProductIds: number[] }>("/home/badges"),

    /** 홈 추천 상품 피드 (위 섹션 제외, 카테고리 번갈아) GET /home/feed */
    getHomeFeed: (page: number) =>
      client.get<ProductListResponse>("/home/feed", { params: { page } }),

    /** 브랜드 정보 GET /brands/information/{brandId} */
    getBrandInfo: (brandId: string) => client.get<BrandInfo>(`/brands/information/${brandId}`),

    /** 브랜드 공지 GET /brandNotice/{brandId}/latest/exposed */
    getBrandNotice: (brandId: string) =>
      client.get<BrandNotice>(`/brandNotice/${brandId}/latest/exposed`),

    /** 찜 토글 POST /products/favorite */
    toggleFavorite: (idProduct: number) =>
      client.post<boolean>("/products/favorite", { idProduct }),

    /** 찜 목록 GET /products/favorite/list — brand names enriched */
    getFavoriteList: async () => {
      const res = await client.get<FavoriteListItem[]>("/products/favorite/list");
      const items = res.data;
      if (!items || items.length === 0) return res;
      const brandMap = await fetchBrandMap(
        client,
        items.map((i) => i.idBrand),
      );
      const enriched = items.map((item) => ({
        ...item,
        brandName: brandMap.get(item.idBrand) ?? "",
      }));
      return { ...res, data: enriched } as typeof res;
    },

    /** 찜 여부 확인 GET /products/favorite/{id} */
    checkFavorite: (productId: string) => client.get<boolean>(`/products/favorite/${productId}`),
  };
}
